import { useEffect } from "react"
import type { ReactNode } from "react"
import { ClickAwayListener, Grow, IconButton, MenuList, Paper, Popper } from "@mui/material"
import type { PopperPlacementType } from "@mui/material"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import { makeStyles } from "tss-react/mui"

import { useHoverMenu } from "../../hooks/useHoverMenu"

const useStyles = makeStyles()(() => ({
    popper: {
        zIndex: 1300,
    },
}))

type HoverActionMenuProps = Readonly<{
    children: (closeMenu: () => void) => ReactNode
    icon?: ReactNode
    iconButtonClassName?: string
    placement?: PopperPlacementType
    onOpen?: (event: React.MouseEvent<HTMLElement>) => void
    /** Reports open/close, e.g. to keep a hover-faded anchor visible while the menu is open. */
    onOpenChange?: (isOpen: boolean) => void
    "aria-label"?: string
}>

export function HoverActionMenu({
    children,
    icon,
    iconButtonClassName,
    placement = "bottom-end",
    onOpen,
    onOpenChange,
    "aria-label": ariaLabel,
}: HoverActionMenuProps) {
    const { classes } = useStyles()
    const { isOpen, anchorEl, handleOpen, handleClose, scheduleClose, cancelClose } = useHoverMenu()

    useEffect(() => {
        onOpenChange?.(isOpen)
    }, [isOpen, onOpenChange])

    const onMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
        onOpen?.(event)
        handleOpen(event)
    }

    const onClick = (event: React.MouseEvent<HTMLElement>) => {
        onOpen?.(event)
        handleOpen(event)
    }

    const onListMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
        if (event.target instanceof Node && !event.currentTarget.contains(event.target)) return
        scheduleClose()
    }

    return (
        <>
            <IconButton
                size="small"
                aria-label={ariaLabel}
                className={iconButtonClassName}
                onMouseEnter={onMouseEnter}
                onMouseLeave={scheduleClose}
                onClick={onClick}
            >
                {icon ?? <MoreVertIcon />}
            </IconButton>
            <Popper open={isOpen} anchorEl={anchorEl} placement={placement} transition className={classes.popper}>
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                        <Paper elevation={8}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList onMouseEnter={cancelClose} onMouseLeave={onListMouseLeave}>
                                    {isOpen && children(handleClose)}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    )
}
